import { useState, useMemo } from 'react';
import {
  Button,
  Card,
  Status,
  Segmented,
} from '@insight-health-ai/roseate/components';
import {
  Edit02,
  Trash01,
  MessageTextSquare01,
  Camera01,
  //   TestTube02,
  Users01,
} from '@insight-health-ai/roseate/icons';
import PrescriptionReview from './PrescriptionReview';
import { useGetSummaryList, useDeleteMedicineSummary } from 'src/hooks/summary';
import { useSubmitMedicationOrders } from 'src/hooks/ehr/useSubmitMedicationOrders';
import { getLatestSummary } from 'src/utils/summary/helpers';
import EditOrderModal from './EditOrderModal';

const EncounterOrders = ({ encounterId }) => {
  const [selectedOrderTab, setSelectedOrderTab] = useState('all');
  const [currentView, setCurrentView] = useState('orders'); // 'orders' or 'prescriptionReview'
  const [approvedOrders, setApprovedOrders] = useState([]); // Dynamic approved orders
  const [rereviewOrderIds, setRereviewOrderIds] = useState(new Set());
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);

  // API hooks
  const deleteMedicineMutation = useDeleteMedicineSummary();
  const {
    mutate: submitMedicationOrders,
    isLoading: isSubmittingMedicationOrders,
  } = useSubmitMedicationOrders();

  // Fetch medicine summaries to get actual prescription count
  const { data: medicineData, isLoading: isLoadingMedicines } =
    useGetSummaryList({
      encounterId,
      encounterPhase: 'in-visit',
      type: 'medicine',
    });

  // Calculate total number of medications from the latest summary version
  const totalMedicationCount = useMemo(() => {
    if (!medicineData?.summaries) return 0;

    const latestSummary = getLatestSummary(medicineData.summaries);

    if (!latestSummary) return 0;

    try {
      const medications = JSON.parse(latestSummary.summary_json || '[]');
      const activeMedications = medications.filter((med) => !med.is_deleted);
      return activeMedications.length;
    } catch (error) {
      console.error('Error parsing medicine summary_json:', error);
      return 0; // Fallback to 0 if parsing fails
    }
  }, [medicineData?.summaries]);

  const pendingOrders = useMemo(
    () => ({
      prescriptions: {
        count: totalMedicationCount - approvedOrders.length,
        description: 'AI-suggested prescription pending review',
        available: true,
      },
      imaging: {
        count: 2,
        description: 'AI-suggested imaging orders pending review',
        available: false,
      },
      referrals: {
        count: 1,
        description: 'AI-suggested referral pending review',
        available: false,
      },
    }),
    [totalMedicationCount, approvedOrders.length]
  );

  const orderTabOptions = [
    { label: 'All Orders', value: 'all' },
    { label: 'Medications', value: 'medication' },
    { label: 'Imaging', value: 'imaging' },
    { label: 'Referrals', value: 'referral' },
  ];

  // Filter approved orders based on the selected tab
  const filteredApprovedOrders =
    selectedOrderTab === 'all'
      ? approvedOrders
      : approvedOrders.filter((order) => order.type === selectedOrderTab);

  /**
   * Navigates to the review screen for a given order type.
   * @param {string} orderType - The type of order to review (e.g., 'prescriptions').
   */
  const handleReview = (orderType) => {
    if (orderType === 'prescriptions') {
      setCurrentView('prescriptionReview');
    }
    // TODO: Implement review for other order types
  };

  /**
   * Returns to the main orders view from a review screen.
   */
  const handleBackToOrders = () => {
    setCurrentView('orders');
  };

  /**
   * Adds an approved prescription to the list of approved orders.
   * @param {Object} prescription - The prescription object from the review screen.
   */
  const handleApprovePrescription = (prescription) => {
    // Convert prescription to approved order format
    const approvedOrder = {
      id: `approved-${Date.now()}`, // Unique ID for local state management
      type: 'medication',
      name: prescription.name,
      dosage: prescription.dosage,
      frequency: prescription.frequency,
      duration: prescription.duration,
      code: prescription.associated_diagnosis_code || 'N/A',
      status: 'Approved',
      order_type_id: prescription.order_type_id, // For API calls
      prescription: prescription, // Keep original prescription data
    };

    setApprovedOrders((prev) => [...prev, approvedOrder]);
    setRereviewOrderIds((prev) => {
      const newSet = new Set(prev);
      newSet.delete(prescription.order_type_id);
      return newSet;
    });
  };

  /**
   * Deletes an approved order from the backend and local state.
   * @param {string} orderId - The ID of the order to delete.
   */
  const handleDelete = async (orderId) => {
    const orderToDelete = approvedOrders.find((o) => o.id === orderId);
    if (!orderToDelete) return;

    // Add to re-review list
    if (orderToDelete.type === 'medication' && orderToDelete.order_type_id) {
      setRereviewOrderIds((prev) =>
        new Set(prev).add(orderToDelete.order_type_id)
      );
    }

    // Remove from local state
    setApprovedOrders((prev) => prev.filter((o) => o.id !== orderId));
  };

  const handleEdit = (orderId) => {
    const orderToEdit = approvedOrders.find((o) => o.id === orderId);
    if (orderToEdit && orderToEdit.type === 'medication') {
      setEditingOrder(orderToEdit);
      setIsEditModalOpen(true);
    }
  };

  const handleOrderUpdated = (orderId, updatedValues) => {
    setApprovedOrders((prev) =>
      prev.map((order) => {
        if (order.id === orderId) {
          const updatedPrescription = {
            ...order.prescription,
            ...updatedValues,
            warnings: (() => {
              if (!updatedValues.warnings) return [];
              if (Array.isArray(updatedValues.warnings))
                return updatedValues.warnings;
              return typeof updatedValues.warnings === 'string'
                ? updatedValues.warnings.split(',').map((s) => s.trim())
                : [];
            })(),
          };
          return {
            ...order,
            name: updatedValues.name,
            dosage: updatedValues.dosage,
            frequency: updatedValues.frequency,
            duration: updatedValues.duration,
            code: updatedValues.associated_diagnosis_code || 'N/A',
            prescription: updatedPrescription,
          };
        }
        return order;
      })
    );
  };

  /**
   * Submits all approved medication orders to the EHR.
   */
  const handleSaveToEHR = () => {
    const medicationOrdersToSubmit = approvedOrders
      .filter((order) => order.type === 'medication')
      .map((order) => order.prescription);

    if (medicationOrdersToSubmit.length > 0) {
      submitMedicationOrders({
        encounterId,
        medication_orders: medicationOrdersToSubmit,
      });
    } else {
      console.log('No medication orders to submit.');
    }
  };

  /**
   * Renders the appropriate icon for a given order type and context.
   * @param {string} type - The order type (e.g., 'medication').
   * @param {string} context - The context ('pending' or 'approved').
   * @returns {React.ReactElement} The icon component.
   */
  const renderOrderIcon = (type, context = 'approved') => {
    const getIconColor = (orderType, orderContext) => {
      const colors = {
        pending: {
          medication: 'text-orange-500',
          imaging: 'text-teal-500',
          referral: 'text-orange-500',
          default: 'text-gray-600',
        },
        approved: {
          medication: 'text-teal-500',
          imaging: 'text-teal-500',
          referral: 'text-orange-500',
          default: 'text-gray-600',
        },
      };
      return colors[orderContext]?.[orderType] || colors[orderContext]?.default;
    };

    const iconProps = {
      className: `w-4 h-4 ${getIconColor(type, context)}`,
    };

    switch (type) {
      case 'medication':
        return <MessageTextSquare01 {...iconProps} />;
      case 'imaging':
        return <Camera01 {...iconProps} />;
      case 'referral':
        return <Users01 {...iconProps} />;
      default:
        return <MessageTextSquare01 {...iconProps} />;
    }
  };

  /**
   * Renders the details section for an approved order card.
   * @param {Object} order - The approved order object.
   * @returns {React.ReactElement|null} The details component.
   */
  const renderApprovedOrderDetails = (order) => {
    console.log('order', order);
    switch (order.type) {
      case 'medication':
        return (
          <div className="text-sm text-gray-500">
            {order.frequency} • {order.duration} • {order.code}
          </div>
        );
      case 'imaging':
        return (
          <div className="text-sm text-gray-500">
            {order.details} • {order.priority} • {order.code}
          </div>
        );
      case 'referral':
        return (
          <div className="text-sm text-gray-500">
            {order.provider} • {order.details} • {order.code}
          </div>
        );
      default:
        return null;
    }
  };

  // Show prescription review screen
  if (currentView === 'prescriptionReview') {
    return (
      <PrescriptionReview
        onBack={handleBackToOrders}
        encounterId={encounterId}
        onApprovePrescription={handleApprovePrescription}
        approvedOrders={approvedOrders}
        rereviewOrderIds={rereviewOrderIds}
        medicineData={medicineData}
        isLoadingMedicines={isLoadingMedicines}
      />
    );
  }

  // Show main orders screen with loading overlay during mutations
  return (
    <div className="h-full w-full p-4 bg-white overflow-y-auto relative">
      {(deleteMedicineMutation.isPending || isSubmittingMedicationOrders) && (
        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
          <div className="text-gray-600">
            {isSubmittingMedicationOrders
              ? 'Submitting orders to EHR...'
              : 'Deleting order...'}
          </div>
        </div>
      )}
      <div className="flex justify-end mb-4">
        <Button
          className="bg-[#5667E9] hover:bg-[#5667E9] hover:!bg-[#5667E9] flex items-center justify-center text-white font-normal p-4 px-4"
          type={'button'}
          size="sm"
          onClick={handleSaveToEHR}
          loading={isSubmittingMedicationOrders}
        >
          Save to EHR
        </Button>
      </div>

      <div className="flex flex-col lg:flex-row gap-6">
        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Pending Orders
          </h2>

          <Card className="mb-3 border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3">
              <div className="w-8 h-8 bg-white border border-gray-200 rounded-md flex items-center justify-center">
                {renderOrderIcon('medication', 'pending')}
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  Pending Prescriptions
                </h3>
                {pendingOrders.prescriptions.available && (
                  <p className="text-sm text-gray-500">
                    {pendingOrders.prescriptions.count}{' '}
                    {pendingOrders.prescriptions.description}
                  </p>
                )}
              </div>
              <Button
                appearance="primary"
                size="sm"
                className={`${
                  pendingOrders.prescriptions.available
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed'
                } w-full sm:w-auto`}
                onClick={() => handleReview('prescriptions')}
                disabled={!pendingOrders.prescriptions.available}
              >
                {pendingOrders.prescriptions.available
                  ? 'Review'
                  : 'Coming soon'}
              </Button>
            </div>
          </Card>

          <Card className="mb-3 border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3">
              <div className="w-8 h-8 bg-white border border-gray-200 rounded-md flex items-center justify-center">
                {renderOrderIcon('imaging', 'pending')}
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  Pending Imaging Orders
                </h3>
                {pendingOrders.imaging.available && (
                  <p className="text-sm text-gray-500">
                    {pendingOrders.imaging.count}{' '}
                    {pendingOrders.imaging.description}
                  </p>
                )}
              </div>
              <Button
                appearance="primary"
                size="sm"
                className={`${
                  pendingOrders.imaging.available
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed'
                } w-full sm:w-auto`}
                onClick={() => handleReview('imaging')}
                disabled={!pendingOrders.imaging.available}
              >
                {pendingOrders.imaging.available ? 'Review' : 'Coming soon'}
              </Button>
            </div>
          </Card>

          <Card className="mb-3 border-gray-200">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3">
              <div className="w-8 h-8 bg-white border border-gray-200 rounded-md flex items-center justify-center">
                {renderOrderIcon('referral', 'pending')}
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 mb-1">
                  Pending Referrals
                </h3>
                {pendingOrders.referrals.available && (
                  <p className="text-sm text-gray-500">
                    {pendingOrders.referrals.count}{' '}
                    {pendingOrders.referrals.description}
                  </p>
                )}
              </div>
              <Button
                appearance="primary"
                size="sm"
                className={`${
                  pendingOrders.referrals.available
                    ? 'bg-green-600 hover:bg-green-700'
                    : 'bg-gray-400 hover:bg-gray-400 cursor-not-allowed'
                } w-full sm:w-auto`}
                onClick={() => handleReview('referrals')}
                disabled={!pendingOrders.referrals.available}
              >
                {pendingOrders.referrals.available ? 'Review' : 'Coming soon'}
              </Button>
            </div>
          </Card>
        </div>

        <div className="flex-1">
          <h2 className="text-xl font-semibold text-gray-900 mb-3">
            Approved Orders
          </h2>
          <div className="mb-4">
            <Segmented
              options={orderTabOptions}
              value={selectedOrderTab}
              onChange={setSelectedOrderTab}
              className="w-full sm:w-auto"
            />
          </div>

          {filteredApprovedOrders.length > 0 ? (
            filteredApprovedOrders.map((order) => (
              <Card
                key={order.id}
                className="mb-3 border-green-200 bg-green-50"
              >
                <div className="flex items-start gap-3 p-3">
                  <div className="w-8 h-8 bg-white border border-green-200 rounded-md flex items-center justify-center flex-shrink-0">
                    {renderOrderIcon(order.type, 'approved')}
                  </div>
                  <div className="flex-1">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-1 gap-2">
                      <h3 className="text-base font-semibold text-gray-900">
                        {order.name} {order.dosage && order.dosage}
                      </h3>
                      <div className="flex items-center gap-2">
                        <button
                          className="text-gray-500 hover:text-gray-700 cursor-pointer"
                          onClick={() => handleEdit(order.id)}
                        >
                          <Edit02 className="w-3 h-3" />
                        </button>
                        <Status
                          size="xs"
                          label="Approved"
                          appearance="success"
                        />
                        <button
                          className="text-red-500 hover:text-red-700 cursor-pointer"
                          onClick={() => handleDelete(order.id)}
                        >
                          <Trash01 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    {renderApprovedOrderDetails(order)}
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <div className="text-center py-6 text-gray-500">
              <div className="text-base mb-1">No approved orders found</div>
              <div className="text-sm">
                {selectedOrderTab === 'all'
                  ? 'No orders have been approved yet.'
                  : `No ${orderTabOptions.find((tab) => tab.value === selectedOrderTab)?.label.toLowerCase()} orders found.`}
              </div>
            </div>
          )}
        </div>
      </div>
      {isEditModalOpen && (
        <EditOrderModal
          open={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setEditingOrder(null);
          }}
          order={editingOrder}
          encounterId={encounterId}
          onOrderUpdated={handleOrderUpdated}
        />
      )}
    </div>
  );
};

export default EncounterOrders;
